import { useCallback, useEffect } from 'react';
import { useHistory } from '@react-router';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useRouterHelper, useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useGetTransitionalRegistryStatus } from '../../hooks/useGetTransitionalRegistryStatus';
import { AddFromFriendsRegistryScannerDialogProps } from './AddFromFriendsRegistryScannerDialog';
import { useFriendsRegistryRoutePaths } from '@apps/registry/common/components/FriendsRegistry/FriendsRegistry.routes';
import { TransitionalRegistryStatus } from '@graphql/generated';
import { useFriendsRegistryTelemetry } from '../../FriendsRegistry.telemetry';

// To Do: Updated videos and thumbnails - https://withjoy.atlassian.net/browse/BABY-720
const VIDEO_URL = 'https://player.vimeo.com/video/896263436?badge=0&autopause=0&player_id=0&app_id=58479&background=1';
const VIDEO_THUMBNAIL_URL = `https://withjoy.com/assets/public/shop/features/add-from-registry-url/desktop_fallback.jpg`;

const MOBILE_VIDEO_URL = 'https://player.vimeo.com/video/901015563?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&background=1';
const MOBILE_VIDEO_THUMBNAIL_URL = `https://withjoy.com/assets/public/shop/features/add-from-registry-url/mobile_fallback.jpg`;

export const useAddFromFriendsRegistryScannerDialogController = ({ id, status, dateString, onClose }: AddFromFriendsRegistryScannerDialogProps) => {
  const history = useHistory();
  const routeHelpers = useRouterHelper();
  const telemetry = useFriendsRegistryTelemetry();
  const { friendsRegistryRoutes } = useFriendsRegistryRoutePaths();
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const videoUrl = isMobile ? MOBILE_VIDEO_URL : VIDEO_URL;
  const videoThumbnailUrl = isMobile ? MOBILE_VIDEO_THUMBNAIL_URL : VIDEO_THUMBNAIL_URL;

  const { statusInfo, setStatusInfo } = useGetTransitionalRegistryStatus({ id });
  const { t2 } = useTranslation('addFromFriendsRegistry');
  const ctaText = t2('scannerDialog', 'ctaText');
  const translations = t2('scannerDialog', statusInfo.status);

  const onCloseDialogHandle = useCallback(() => {
    telemetry.closeRegistryScannerDialog('affrScannerDialog_userClosed', id, statusInfo.status);
    onClose();
  }, [id, statusInfo.status, telemetry, onClose]);

  const handleOnClick = useEventCallback(() => {
    telemetry.selectGiftsRegistryScannerDialog(id);
    history.push({ pathname: routeHelpers.buildPath(`${friendsRegistryRoutes.addToFriendsRegistryIndex.path}/${id}`) });
  });

  useEffect(() => {
    if (statusInfo.status === TransitionalRegistryStatus.failed) {
      telemetry.closeRegistryScannerDialog('affrScannerDialog_scanFailed', id, statusInfo.status);
      onClose();
    }
  }, [id, statusInfo.status, telemetry, onClose]);

  useEffect(() => setStatusInfo({ status, dateString }), [id, status, dateString, setStatusInfo]);

  return {
    statusInfo,
    videoUrl,
    videoThumbnailUrl,
    ctaText,
    translations,
    handleOnClick,
    onCloseDialogHandle
  };
};
